import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import './LastMinuteSpotViewer.css';

const LastMinuteSpotViewer = ({ tournamentId, isPublic = false }) => {
  const [tournament, setTournament] = useState(null);
  const [spots, setSpots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [reserving, setReserving] = useState(null);
  const [success, setSuccess] = useState(null);

  const { authenticatedFetch, user, member } = useAuth();

  useEffect(() => {
    loadAvailableSpots();
  }, [tournamentId]);

  const loadAvailableSpots = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const endpoint = isPublic 
        ? `/api/tournaments/${tournamentId}/last-minute-spots/public`
        : `/api/tournaments/${tournamentId}/last-minute-spots`;
      
      const response = await authenticatedFetch(endpoint);
      const data = await response.json();
      
      if (data.success) {
        setTournament(data.data.tournament);
        setSpots(data.data.spots);
      } else {
        throw new Error(data.error || 'Failed to load available spots');
      }
    } catch (err) {
      console.error('Error loading available spots:', err);
      setError(err.message || 'Failed to load available spots');
    } finally {
      setLoading(false);
    }
  };

  const reserveSpot = async (spotId, spot) => {
    if (!member) {
      setError('You must be logged in as a member to reserve spots');
      return;
    }

    try {
      setReserving(spotId);
      setError(null);
      
      const response = await authenticatedFetch(`/api/last-minute-spots/${spotId}/reserve`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          memberId: member.id,
          specialRequests: ''
        })
      });
      
      const data = await response.json();
      
      if (data.success) {
        setSuccess(`✅ Successfully reserved ${spot.teeTime} Group ${spot.groupNumber} Position ${spot.position}!`);
        
        // Remove the spot from available spots
        setSpots(prev => prev.filter(s => s._id !== spotId));
        
        // Clear success message after 5 seconds
        setTimeout(() => setSuccess(null), 5000);
      } else {
        throw new Error(data.error || 'Failed to reserve spot');
      }
    } catch (err) {
      console.error('Error reserving spot:', err);
      setError(err.message || 'Failed to reserve spot');
    } finally {
      setReserving(null);
    }
  };

  const getPopularityClass = (popularity) => {
    switch (popularity) {
      case 'hot': return 'spot-hot';
      case 'competitive': return 'spot-competitive';
      case 'available': return 'spot-available';
      default: return 'spot-available';
    }
  };

  const getPopularityIcon = (popularity, requestCount) => {
    switch (popularity) {
      case 'hot': return `🔥 POPULAR (${requestCount} requests)`;
      case 'competitive': return `🟡 COMPETITIVE (${requestCount} requests)`;
      case 'available': return '🟢 AVAILABLE';
      default: return '🟢 AVAILABLE';
    }
  };

  if (loading) {
    return (
      <div className="spot-viewer loading">
        <div className="loading-spinner"></div>
        <p>Loading available spots...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="spot-viewer error">
        <h2>❌ Unable to Load Spots</h2>
        <p>{error}</p>
        <button onClick={loadAvailableSpots} className="retry-button">
          Try Again
        </button>
      </div>
    );
  }

  if (!tournament) {
    return (
      <div className="spot-viewer error">
        <h2>Tournament Not Found</h2>
        <p>The requested tournament could not be found.</p>
      </div>
    );
  }

  if (spots.length === 0) {
    return (
      <div className="spot-viewer empty">
        <h2>🏌️ No Available Spots</h2>
        <p>There are currently no last-minute spots available for <strong>{tournament.name}</strong>.</p>
        <p>Check back later or contact the tournament director for more information.</p>
      </div>
    );
  }

  return (
    <div className="spot-viewer">
      <div className="tournament-header">
        <h1>🕐 Last-Minute Spots Available</h1>
        <div className="tournament-info">
          <h2>{tournament.name}</h2>
          <p>
            📅 {new Date(tournament.date).toLocaleDateString()} at {tournament.startTime}
          </p>
        </div>
      </div>

      {success && (
        <div className="success-banner">
          {success}
        </div>
      )}

      {error && (
        <div className="error-banner">
          {error}
        </div>
      )}

      <div className="spots-grid">
        <h3>📍 Available Positions - Click to Reserve</h3>
        <p className="spots-instructions">
          Click on any spot below to request it. Your reservation will be pending tournament director approval.
        </p>
        
        <div className="spots-list">
          {spots.map((spot) => (
            <div key={spot._id} className={`spot-card ${getPopularityClass(spot.popularity)}`}>
              <div className="spot-header">
                <div className="spot-time">
                  <span className="time">{spot.teeTime}</span>
                  <span className="group">Group {spot.groupNumber}</span>
                  <span className="position">Position {spot.position}</span>
                </div>
                <div className="spot-popularity">
                  {getPopularityIcon(spot.popularity, spot.requestCount)}
                </div>
              </div>
              
              <div className="spot-actions">
                <button
                  onClick={() => reserveSpot(spot._id, spot)}
                  disabled={reserving === spot._id}
                  className="reserve-button"
                >
                  {reserving === spot._id ? (
                    <>
                      <span className="spinner"></span>
                      Reserving...
                    </>
                  ) : (
                    <>
                      🎯 Reserve This Spot
                    </>
                  )}
                </button>
              </div>
              
              {spot.requestCount > 0 && (
                <div className="spot-competition">
                  ⚠️ {spot.requestCount} {spot.requestCount === 1 ? 'person has' : 'people have'} already requested this spot
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      <div className="viewer-footer">
        <div className="legend">
          <h4>Legend:</h4>
          <div className="legend-items">
            <span className="legend-item">🟢 Available - Good option</span>
            <span className="legend-item">🟡 Competitive - Some competition</span>
            <span className="legend-item">🔥 Popular - High demand</span>
          </div>
        </div>
        
        <p className="disclaimer">
          <strong>Note:</strong> Reservations are pending tournament director approval. 
          You will be notified of your final tee time once spots are assigned.
        </p>
      </div>
    </div>
  );
};

export default LastMinuteSpotViewer;
